import React, { useState, useEffect } from 'react';
import { Search, Eye, Loader2, RefreshCw, AlertCircle, CheckCircle, XCircle, DollarSign, Package, UserPlus } from 'lucide-react';
import { Retailer } from '../../types';
import { getRetailers } from '../../services/retailerService';
import RetailerDetailModal from './RetailerDetailModal';
import AddRetailerModal from './AddRetailerModal';

interface RetailersTabProps {
  adminEmail: string;
}

const RetailersTab: React.FC<RetailersTabProps> = ({ adminEmail }) => {
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRetailer, setSelectedRetailer] = useState<Retailer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended'>('all');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadRetailers();
  }, []);

  const loadRetailers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getRetailers();
      setRetailers(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load retailers');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredRetailers = retailers.filter(r => {
    const matchesSearch = searchTerm === '' ||
      r.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    if (status === 'active') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] tracking-micro uppercase bg-olive/15 text-olive border border-olive/40">
          <CheckCircle className="w-3 h-3" strokeWidth={1.5} />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] tracking-micro uppercase bg-ember-soft/25 text-ember border border-ember/30">
        <XCircle className="w-3 h-3" strokeWidth={1.5} />
        Suspended
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-ember" strokeWidth={1.5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-ember-soft/25 border-l-2 border-ember p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
        <div>
          <p className="text-ember-deep font-medium text-sm">Error loading retailers</p>
          <p className="text-ember text-sm">{error}</p>
          <button onClick={loadRetailers} className="mt-2 text-ember-deep underline text-sm hover:text-ember transition-colors duration-150">
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Calculate totals
  const totalRevenue = retailers.reduce((sum, r) => sum + r.totalSpent, 0);
  const totalOrders = retailers.reduce((sum, r) => sum + r.totalOrders, 0);

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-3">— Retailers</p>
          <h2 className="font-editorial italic font-light text-3xl text-ink-950 tracking-tight">
            Active retailers.
          </h2>
          <p className="text-ink-700 text-sm mt-1">Manage approved retailer accounts.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadRetailers}
            className="inline-flex items-center gap-2 px-4 py-2 text-ink-700 hover:text-ink-950 hover:bg-paper-bright border border-ink-200 hover:border-ink-300 text-sm font-medium transition-colors duration-150"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
            Refresh
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 bg-ember hover:bg-ember-deep text-paper-warm px-5 py-2.5 text-sm font-medium transition-colors duration-150 ease-editorial"
          >
            <UserPlus className="w-4 h-4" strokeWidth={1.5} />
            Add retailer
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-paper-bright border border-ink-200 p-5">
          <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Total retailers</p>
          <p className="font-editorial italic text-2xl text-ink-950">{retailers.length}</p>
        </div>
        <div className="bg-paper-bright border border-ink-200 p-5">
          <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Active</p>
          <p className="font-editorial italic text-2xl text-olive">
            {retailers.filter(r => r.status === 'active').length}
          </p>
        </div>
        <div className="bg-paper-bright border border-ink-200 p-5 flex items-center gap-3">
          <div className="w-10 h-10 bg-steel/10 flex items-center justify-center">
            <Package className="w-5 h-5 text-steel" strokeWidth={1.5} />
          </div>
          <div>
            <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-0.5">Total orders</p>
            <p className="font-editorial italic text-xl text-ink-950">{totalOrders}</p>
          </div>
        </div>
        <div className="bg-paper-bright border border-ink-200 p-5 flex items-center gap-3">
          <div className="w-10 h-10 bg-olive/10 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-olive" strokeWidth={1.5} />
          </div>
          <div>
            <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-0.5">Revenue</p>
            <p className="font-editorial italic text-xl text-ink-950">${totalRevenue.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" strokeWidth={1.5} />
          <input
            type="text"
            placeholder="Search by business name, contact, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150"
          />
        </div>
        <div className="flex gap-2">
          {(['all', 'active', 'suspended'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors duration-150 border ${
                statusFilter === status
                  ? 'bg-ember text-paper-warm border-ember'
                  : 'bg-paper-bright text-ink-700 border-ink-200 hover:border-ink-300 hover:bg-paper'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {filteredRetailers.length === 0 ? (
        <div className="bg-paper-bright border border-ink-200 p-8 text-center">
          <p className="text-ink-500 text-sm">No retailers found.</p>
        </div>
      ) : (
        <div className="bg-paper-bright border border-ink-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-paper border-b border-ink-200">
              <tr>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Business</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Contact</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Location</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Orders</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Total spent</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Status</th>
                <th className="px-4 py-3 text-right font-mono text-[10px] tracking-micro uppercase text-ink-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200">
              {filteredRetailers.map((retailer) => (
                <tr key={retailer.id} className="hover:bg-paper transition-colors duration-150">
                  <td className="px-4 py-4">
                    <div>
                      <p className="font-medium text-ink-950 text-sm">{retailer.businessName}</p>
                      <p className="text-xs text-ink-500 font-mono mt-0.5">{retailer.taxId}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div>
                      <p className="text-ink-900 text-sm">{retailer.contactName}</p>
                      <p className="text-xs text-ink-500 mt-0.5">{retailer.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-ink-700 text-sm">
                    {retailer.address.city}, {retailer.address.province}
                  </td>
                  <td className="px-4 py-4 text-ink-950 font-medium text-sm">
                    {retailer.totalOrders}
                  </td>
                  <td className="px-4 py-4 text-ink-950 font-medium text-sm">
                    ${retailer.totalSpent.toLocaleString()}
                  </td>
                  <td className="px-4 py-4">
                    {getStatusBadge(retailer.status)}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button
                      onClick={() => setSelectedRetailer(retailer)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-ember hover:text-ember-deep hover:bg-ember-soft/25 transition-colors duration-150"
                    >
                      <Eye className="w-4 h-4" strokeWidth={1.5} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedRetailer && (
        <RetailerDetailModal
          retailer={selectedRetailer}
          onClose={() => setSelectedRetailer(null)}
          onUpdate={loadRetailers}
        />
      )}

      {/* Add Retailer Modal */}
      {showAddModal && (
        <AddRetailerModal
          adminEmail={adminEmail}
          onClose={() => setShowAddModal(false)}
          onSuccess={loadRetailers}
        />
      )}
    </div>
  );
};

export default RetailersTab;
